import {Button, Input} from "antd";
import CopyToClipboardField from "../../copyToClipboardField/CopyToClipboardField";
import CopyToClipboardCode from "../../copyToClipboardCode/CopyToClipboardCode";
import ButtonGroup from "../../forms/buttonGroup/ButtonGroup";
import React, {useEffect, useState} from "react";
import useUserContext from "../../../hooks/useUserContext";
import {getIntegrationKey} from "../../../api/getIntegrationKey";
import {ConsentIntegrationOptions} from "../ConsentIntegrationSection";
import {setIntegrationKey} from "../../../api/setIntegrationKey";
import useToast from "../../../hooks/useToast";
import {ToastSkin} from "../../../context/ToastContext";
import {getKlaviyoCallbackUrlToken} from "../../../api/getKlaviyoCallbackUrlToken";
import {LinkOutlined} from "@ant-design/icons";

const KlaviyoIntegrationForm = ({ isConnected, setIsConnected }: { isConnected: boolean; setIsConnected: (newVal: boolean) => void }) => {
    const [selectedHook, setSelectedHook] = useState('email-subscribe');
    const [apiKey, setApiKey] = useState('');
    const [callbackUrlToken, setCallbackUrlToken] = useState('');
    const { selectedDomain } = useUserContext();
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);

    const getInitialApiKey = async () => {
        if(selectedDomain) {
            const apiKeyResponse = await getIntegrationKey(selectedDomain, ConsentIntegrationOptions.KLAVIYO);
            const callbackUrlTokenResponse = await getKlaviyoCallbackUrlToken(selectedDomain);
            setApiKey(apiKeyResponse.data || '');
            setCallbackUrlToken(callbackUrlTokenResponse.data || '');
        }
    }

    const submitApiKey = async () => {
        if(selectedDomain) {
            setIsLoading(true);
            try {
                const response = await setIntegrationKey(selectedDomain, ConsentIntegrationOptions.KLAVIYO, apiKey);
                if (response.data === 'ok') {
                    setIsConnected(true);
                    showToast({text: 'Api Key was updated successfully!', skin: ToastSkin.approval});
                } else {
                    showToast({
                        text: 'Api Key failed to be updated, please try again or contact support',
                        skin: ToastSkin.error
                    });
                }
                setIsLoading(false);
            } catch(error: any) {
                setIsLoading(false);
            }
        }
    }

    useEffect(() => {
        getInitialApiKey();
    }, [selectedDomain]);

    return (
        <div className="inner-integration-container">
            <div className="left">
                <div className="title">1. Create a Klaviyo API Key</div>
                <ul>
                    <li>Log in to Klaviyo (owner/admin).</li>
                    <li>Go to Account → Settings → API Keys.</li>
                    <li>Click Create Private API Key.</li>
                    <li>Name it e.g. "Vuelve Integration" and create it.</li>
                    <li>Copy the key (looks like pk_...), paste it below and click on Submit</li>
                </ul>
                <div className="integration-form">
                    <Input value={apiKey} onChange={(e: any) => setApiKey(e.target.value)} placeholder='Klaviyo API-Key' />
                </div>
                <div className="actions">
                    <Button loading={isLoading} type="primary" onClick={submitApiKey}>Submit</Button>
                    {isConnected ? <div className="success-indicator"><LinkOutlined /><span>Connected</span></div> : null}
                </div>
            </div>
            <div className="right">
                <div className="title">2. Send updates via Klaviyo Flow Webhooks</div>
                <div className="instructions">
                    <div>Create four Flows, each with a Webhook action:</div>
                    <ButtonGroup items={[
                        {id: 'email-subscribe', title: 'Email Subscribe', isSelected: selectedHook === 'email-subscribe'},
                        {id: 'email-unsubscribe', title: 'Email Unsubscribe', isSelected: selectedHook === 'email-unsubscribe'},
                        {id: 'sms-subscribe', title: 'SMS Subscribe', isSelected: selectedHook === 'sms-subscribe'},
                        {id: 'sms-unsubscribe', title: 'SMS Unsubscribe', isSelected: selectedHook === 'sms-unsubscribe'},
                    ]} onClick={(newVal) => setSelectedHook(newVal as string)} />
                    <ul>
                        <li>In Klaviyo, navigate to the Flows page</li>
                        <li>Click on "Create Flow" → "Build Your Own"</li>
                        <li>Click on "Your Metrics" and select "Klaviyo"</li>
                        <li>Select trigger: <b>"{selectedHook === 'email-subscribe' ? 'Subscribed to List' : selectedHook === 'email-unsubscribe' ? 'Unsubscribed from List' : selectedHook === 'sms-subscribe' ? 'Subscribed to SMS Marketing' : 'Unsubscribed from SMS Marketing'}"</b></li>
                        <li>Add Webhook action and paste the Vuelve URL:
                            <CopyToClipboardField content={`https://vuelve-server.fly.dev/webhook/klaviyo/${callbackUrlToken}`} />
                        </li>
                        <li>Drag the action to the trigger screen, then continue with the webhook settings</li>
                        <li>Method: POST</li>
                        <li>Headers: Content-Type: application/json</li>
                        <li><div>Body (JSON):</div>
                            <CopyToClipboardCode content={
`{
    "consent_${selectedHook === 'email-subscribe' || selectedHook === 'email-unsubscribe' ? 'email' : 'sms'}": ${selectedHook === 'email-subscribe' || selectedHook === 'sms-subscribe' ? 'true' : 'false'},
    "email": "{{ person.email }}",
    "phone": "{{ person.phone_number }}",
    "first_name": "{{ person.first_name }}",
    "last_name": "{{ person.last_name }}",
    "country": "{{ person.location.country }}",
    "occurred_at": "{{ event.timestamp }}"
}`}
                            />
                        </li>

                    </ul>
                </div>
            </div>
        </div>
    );
}

export default KlaviyoIntegrationForm;